import React from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import type { Category } from '../types/category';
import AdminCategoryAttributeField from './AdminCategoryAttributeField';
import { getEditableCategoryAttributeEntries } from '../utils/announcementObjectAttributesForm';

type Props = {
  category: Category | null;
  objectAttributes: Record<string, string>;
  secretClues: string;
  publicTransportJson: string;
  possibleLossLocationsJson: string;
  extraAttributesJson: string;
  onAttributeChange: (key: string, value: string) => void;
  onSecretCluesChange: (value: string) => void;
  onPublicTransportJsonChange: (value: string) => void;
  onPossibleLossLocationsJsonChange: (value: string) => void;
  onExtraAttributesJsonChange: (value: string) => void;
};

export default function AnnouncementCategoryAttributeFields({
  category,
  objectAttributes,
  secretClues,
  publicTransportJson,
  possibleLossLocationsJson,
  extraAttributesJson,
  onAttributeChange,
  onSecretCluesChange,
  onPublicTransportJsonChange,
  onPossibleLossLocationsJsonChange,
  onExtraAttributesJsonChange,
}: Props) {
  const attributeEntries = getEditableCategoryAttributeEntries(category);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Caractéristiques de l&apos;objet
        {category?.name ? ` — ${category.name}` : ''}
      </Typography>

      {!category ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sélectionnez une catégorie pour éditer les attributs (type de sac, couleur, etc.).
        </Typography>
      ) : attributeEntries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cette catégorie n&apos;a pas d&apos;attributs configurés.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {attributeEntries.map(([key, attribute]) => (
            <Grid item xs={12} sm={attribute.type === 'text' ? 12 : 6} key={key}>
              <AdminCategoryAttributeField
                attributeKey={key}
                attribute={attribute}
                value={objectAttributes[key] || ''}
                dependentValues={objectAttributes}
                onChange={(value) => onAttributeChange(key, value)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Détails secrets"
            fullWidth
            multiline
            minRows={3}
            value={secretClues}
            onChange={(e) => onSecretCluesChange(e.target.value)}
            placeholder={category?.secret_clues_placeholder || 'Indices visibles uniquement par le propriétaire'}
            helperText="Équivalent du champ secret_clues sur le front."
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Transport en commun (JSON)"
            fullWidth
            multiline
            minRows={4}
            value={publicTransportJson}
            onChange={(e) => onPublicTransportJsonChange(e.target.value)}
            helperText='Ex. {"transport_mode":"metro","line":"1","name":"Châtelet"}'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Lieux de perte possibles (JSON)"
            fullWidth
            multiline
            minRows={4}
            value={possibleLossLocationsJson}
            onChange={(e) => onPossibleLossLocationsJsonChange(e.target.value)}
            helperText="Tableau JSON des lieux de perte partenaire."
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Attributs supplémentaires (JSON)"
            fullWidth
            multiline
            minRows={3}
            value={extraAttributesJson}
            onChange={(e) => onExtraAttributesJsonChange(e.target.value)}
            helperText="Clés hors schéma catégorie (rare). Laisser vide si inutile."
          />
        </Grid>
      </Grid>
    </Box>
  );
}
